/**
 * Verification Item Editor Dialog
 *
 * Dialog for creating and editing verification items.
 *
 * Eleven fields on react-hook-form + Zod. Three shapes of control live here:
 *   - text and select fields go through FormField, so a rule that fails has
 *     somewhere to say so;
 *   - the classification checklist and the method picker are custom widgets
 *     with no message slot, driven by watch + setValue. Their arrays are
 *     ORDERED BY THE OPERATOR'S CLICKS and that order reaches the API, so the
 *     handlers below reproduce the shipped filter-then-append exactly rather
 *     than rebuilding the lists from the declared key order.
 *
 * There is deliberately no <form> element: the shipped dialog had none, and
 * adding one would make Enter in any of the seven text inputs submit an
 * eleven-field editor that never submitted on Enter before.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { useConfirm } from '@hooks/useConfirm';
import { VerificationItem } from '../../types/verification';
import ClassificationChecklist from './item-editor/ClassificationChecklist';
import DrbfmTraceAlert from './item-editor/DrbfmTraceAlert';
import VerificationMethodPicker from './item-editor/VerificationMethodPicker';
import { REVIEW_NONE, type ItemFormData, type MethodRank } from './item-editor/types';
import {
  emptyItemForm,
  REVIEW_JUDGMENTS,
  verificationItemSchema,
  type VerificationItemFormValues,
} from '@/features/verification/schemas/verificationItem.schema';

export type { ItemFormData };

type ItemForm = UseFormReturn<VerificationItemFormValues>;
type ReviewJudgment = VerificationItemFormValues['reviewJudgment'];
type ListName =
  | 'classifications'
  | 'verificationMethodsPrimary'
  | 'verificationMethodsSecondary';

const REVIEW_VALUES: readonly string[] = REVIEW_JUDGMENTS;

/**
 * `VerificationItem.reviewJudgment` is `string | null`, but the column's CHECK
 * admits only the two judgments or NULL and Pydantic forwards whatever it is
 * handed — so re-posting anything else would come back a 500. Off-list values
 * (including '', which the shipped select already displayed as blank) seed as
 * null, which keeps the row saveable.
 */
const asReviewJudgment = (value: string | null): ReviewJudgment =>
  value !== null && REVIEW_VALUES.includes(value) ? (value as ReviewJudgment) : null;

const seedFrom = (item: VerificationItem | null | undefined): VerificationItemFormValues =>
  item
    ? {
        specification: item.specification,
        itemName: item.itemName,
        classifications: [...item.classifications],
        standardTarget: item.standardTarget,
        verificationMethodsPrimary: [...item.verificationMethodsPrimary],
        verificationMethodsSecondary: [...item.verificationMethodsSecondary],
        qualityRecordNo: item.qualityRecordNo,
        qualityRecordTitle: item.qualityRecordTitle,
        reviewJudgment: asReviewJudgment(item.reviewJudgment),
        qualityRecordNoRight: item.qualityRecordNoRight,
        qualityRecordTitleRight: item.qualityRecordTitleRight,
      }
    : emptyItemForm();

interface ItemTextFieldProps {
  form: ItemForm;
  name: 'specification' | 'itemName' | 'qualityRecordNo' | 'qualityRecordTitle'
    | 'qualityRecordNoRight' | 'qualityRecordTitleRight';
  label: string;
  required?: boolean;
  className?: string;
}

/** Label ships leading-none; kanji needs 1.5 as a floor, as the Field primitive did. */
const ItemTextField: React.FC<ItemTextFieldProps> = ({
  form,
  name,
  label,
  required,
  className,
}) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field }) => (
      <FormItem className={className} required={required}>
        <FormLabel className="revy-cjk leading-[1.5]">
          {label}
        </FormLabel>
        <FormControl>
          <Input {...field} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

interface VerificationItemEditorProps {
  open: boolean;
  item?: VerificationItem | null;
  unitId: string;
  onSave: (data: ItemFormData) => void;
  onClose: () => void;
}

const VerificationItemEditor: React.FC<VerificationItemEditorProps> = ({
  open,
  item,
  unitId,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const isEditing = !!item;
  // unitId is received from parent for context but not used directly in the editor form
  void unitId;

  const recordNo = t('verification.table.qualityRecordNo');
  const recordTitle = t('verification.table.qualityRecordTitle');
  const right = t('verification.right');

  // No transform or pipe in the schema, so input and output coincide and the
  // single generic is correct; a numeric field would force the three-generic form.
  const form = useForm<VerificationItemFormValues>({
    resolver: zodResolver(verificationItemSchema),
    defaultValues: emptyItemForm(),
  });

  // reset(), not setValue: it re-baselines dirty tracking, so a freshly opened
  // dialog is clean. Same [item, open] trigger the useState form used.
  const { reset } = form;
  React.useEffect(() => {
    reset(seedFrom(item));
  }, [item, open, reset]);

  // Read at render scope: formState is a Proxy that only tracks a flag once it
  // has been read during render. First reading isDirty inside dismiss() would
  // freeze it and the discard prompt would silently stop appearing.
  const { isDirty } = form.formState;

  const [classifications, primaryMethods, secondaryMethods] = form.watch([
    'classifications',
    'verificationMethodsPrimary',
    'verificationMethodsSecondary',
  ]);

  const setList = (name: ListName, value: string[]) =>
    form.setValue(name, value, { shouldDirty: true });

  const toggleClassification = (key: string) =>
    setList(
      'classifications',
      classifications.includes(key)
        ? classifications.filter((c) => c !== key)
        : [...classifications, key],
    );

  const rankOf = (key: string): MethodRank => {
    if (primaryMethods.includes(key)) return 'primary';
    if (secondaryMethods.includes(key)) return 'secondary';
    return 'none';
  };

  // Filter from both, then append to the chosen one — the ranks are mutually
  // exclusive and the append is what makes the array order the click order.
  const setMethodRank = (key: string, rank: MethodRank) => {
    const primary = primaryMethods.filter((m) => m !== key);
    const secondary = secondaryMethods.filter((m) => m !== key);
    if (rank === 'primary') primary.push(key);
    if (rank === 'secondary') secondary.push(key);
    setList('verificationMethodsPrimary', primary);
    setList('verificationMethodsSecondary', secondary);
  };

  const save = form.handleSubmit((values) => onSave(values));

  const dismiss = async () => {
    if (
      isDirty &&
      !(await confirm({
        title: t('confirmation.discardChangesTitle'),
        description: t('confirmation.unsavedChanges'),
        confirmLabel: t('confirmation.discardChangesConfirm'),
        destructive: false,
      }))
    ) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) void dismiss();
      }}
    >
      {/* The inset keeps the MUI dialog's viewport gutter, which `w-full` alone drops. */}
      <DialogContent
        className="revy-ui grid-rows-[auto_minmax(0,1fr)_auto] gap-0 w-[calc(100%-2rem)] max-h-[85vh] p-0 max-w-[900px] sm:max-w-[900px]"
        aria-describedby={undefined}
      >
        <DialogHeader className="p-6">
          <DialogTitle className="revy-cjk text-lg leading-[1.5]">
            {isEditing ? t('verification.editItem') : t('verification.addItem')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div className="min-h-0 overflow-y-auto border-y p-6">
            <div className="flex flex-col gap-4">
              <ItemTextField
                form={form}
                name="specification"
                label={t('verification.table.specification')}
              />
              <ItemTextField
                form={form}
                name="itemName"
                label={t('verification.table.itemName')}
                required
              />

              <ClassificationChecklist
                selected={classifications}
                onToggle={toggleClassification}
              />

              <Separator />

              <FormField
                control={form.control}
                name="standardTarget"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="revy-cjk leading-[1.5]">
                      {t('verification.table.standardTarget')}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} className="revy-cjk leading-[1.7]" />
                    </FormControl>
                    <FormDescription className="revy-cjk text-xs leading-[1.7] tracking-[0.02em]">
                      {t('verification.standardTargetHelp')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              <VerificationMethodPicker rankOf={rankOf} onChange={setMethodRank} />

              <Separator />

              <div className="flex gap-4">
                <ItemTextField
                  form={form}
                  name="qualityRecordNo"
                  label={recordNo}
                  className="flex-1"
                />
                <ItemTextField
                  form={form}
                  name="qualityRecordTitle"
                  label={recordTitle}
                  className="flex-1"
                />
              </div>

              <FormField
                control={form.control}
                name="reviewJudgment"
                render={({ field }) => (
                  <FormItem className="max-w-[200px]">
                    <FormLabel className="revy-cjk leading-[1.5]">
                      {t('verification.table.review')}
                    </FormLabel>
                    <Select
                      // Radix SelectItem throws on value="", so the blank
                      // judgment needs a sentinel; null is the only falsy state
                      // left, '' having been coerced away at seed time.
                      value={field.value ?? REVIEW_NONE}
                      onValueChange={(value) =>
                        field.onChange(value === REVIEW_NONE ? null : value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="revy-ui">
                        <SelectItem value={REVIEW_NONE}>-</SelectItem>
                        <SelectItem value="required">
                          {t('verification.reviewRequired')}
                        </SelectItem>
                        <SelectItem value="not_required">
                          {t('verification.reviewNotRequired')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-4">
                <ItemTextField
                  form={form}
                  name="qualityRecordNoRight"
                  label={`${recordNo} (${right})`}
                  className="flex-1"
                />
                <ItemTextField
                  form={form}
                  name="qualityRecordTitleRight"
                  label={`${recordTitle} (${right})`}
                  className="flex-1"
                />
              </div>
            </div>

            {item?.sourceDrbfmSessionId && (
              <DrbfmTraceAlert sourceDrbfmSessionId={item.sourceDrbfmSessionId} />
            )}
          </div>

          <DialogFooter className="gap-2 p-6 sm:gap-0">
            <Button variant="outline" onClick={onClose}>
              <span className="revy-cjk">{t('common.cancel')}</span>
            </Button>
            {/*
              Ungated, exactly as shipped: the resolver is what stops a blank
              item now. formState.isValid is not usable here — it is only
              accurate under mode:'onChange', which would make errors appear
              while the operator is still typing.
            */}
            <Button onClick={() => void save()}>
              <span className="revy-cjk">{t('common.save')}</span>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationItemEditor;
